/**
 * usePlanWizardHandlers — Groups A and D from usePlanOrchestration.
 *
 * Group A: Wizard message handlers (skill, agent, knowledge, workflow, data, page-design)
 *          plus intent classification helpers.
 * Group D: Artifact confirmation handlers (confirmSkillCreation, confirmAgentCreation,
 *          confirmWorkflowCreation, confirmDataSchemaCreation) and tryAutoActivate.
 */
import { useCallback } from 'react';
import { usePlanStore } from '@/stores/ai/plan-store';
import { useDebugStore } from '@/stores/ai/debug-store';
import { planApi } from '@/services/api/agentApi';
import { EditorContextSerializer } from '@/services/editor/EditorContextSerializer';
import type { SchemaVisibility, Schema } from '@/services/api/schemaApi';
import { FIELD_TYPES } from '@/services/api/schemaApi';
import { GuidedFlowSchema } from '@/schemas/storage';
import type { Component } from '@/types';
import type { PlanPipelineMode, PlanPipelineStage } from '@/types/plan';
import type { PlanOrchestrationDeps } from './usePlanOrchestration';
import { isAbortError, toErrMsg } from './planHelpers';
import { normalizeAutomationProposal } from '@/services/automation/automationProposalSafety';

/** Safely parse guided flow from sessionStorage. */
function parseGuidedFlow() {
  try {
    const raw = sessionStorage.getItem('terramatrix_guided_flow');
    if (!raw) {
      return {};
    }

    const parsed: unknown = JSON.parse(raw);
    const result = GuidedFlowSchema.safeParse(parsed);
    return result.success ? result.data : {};
  } catch {
    return {};
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Interfaces
// ─────────────────────────────────────────────────────────────────────────────

export interface WizardHandlersResult {
  handleSkillWizardMessage: (input: string) => Promise<void>;
  handleAgentWizardMessage: (input: string) => Promise<void>;
  handleKnowledgeWizardMessage: (input: string) => Promise<void>;
  handleWorkflowWizardMessage: (input: string) => Promise<void>;
  handleDataWizardMessage: (input: string) => Promise<void>;
  handlePageDesignWizardMessage: (input: string) => Promise<void>;
  tryAutoActivate: (input: string) => Promise<boolean>;
  confirmSkillCreation: (activate?: boolean) => Promise<void>;
  confirmAgentCreation: (activate?: boolean) => Promise<void>;
  confirmWorkflowCreation: (publish?: boolean) => Promise<void>;
  confirmDataSchemaCreation: () => Promise<void>;
  // Expose classifyWizardIntent and INTENT_TO_PIPELINE so handlePlanMessage (parent) can use them
  classifyWizardIntent: (input: string) => Promise<{ intent: string; confidence: number; domainHint: string | null }>;
  INTENT_TO_PIPELINE: Record<string, PlanPipelineMode | null>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function usePlanWizardHandlers(
  deps: PlanOrchestrationDeps,
  advanceStage: (stage: PlanPipelineStage) => void,
  addUIMessage: (text: string, role: 'assistant' | 'user', meta?: Record<string, unknown>) => void,
  buildSchemaContext: (ps: ReturnType<typeof usePlanStore.getState>) => Promise<Array<{ name: string; groupName: string; fields: Array<{ name: string; type: string; required?: boolean; description?: string }> }> | undefined>,
  runQuickModeCompile: (signal: AbortSignal) => Promise<void>,
  createAbortController: () => AbortController,
): WizardHandlersResult {
  const planStore = usePlanStore();
  const debug = useDebugStore;

  // ── Wizard Intent Classification (Hybrid: Tier 1 Regex → Tier 2 LLM) ─────

  // Tier 1: Fast regex patterns for obvious matches (instant, no LLM call)
  const WIZARD_REGEX_MAP: Record<string, RegExp[]> = {
    app: [
      /\b(build|create|make|develop|design)\b.*\b(app|application|platform|tool|system|product)\b/i,
      /\b(i want|i need|help me)\b.*\b(app|application|platform|tool)\b/i,
    ],
    knowledge: [
      /\b(create|build|compile|write|start)\b.*\b(knowledge|knowledge base|kb)\b/i,
      /\bnew knowledge base\b/i,
    ],
    skill: [
      /\b(create|build|make|define|write|author)\b.*\b(skill|agent skill|new skill)\b/i,
      /\bnew skill\b/i,
      /\bskill\b.*\b(for|that|to|which)\b/i,
    ],
    agent: [
      /\b(create|build|make|define|deploy)\b.*\b(agent|bot|assistant|monitor)\b/i,
      /\bnew agent\b/i,
    ],
    workflow: [
      /\b(create|build|make|define|set up|design)\b.*\b(workflow|automation|pipeline|rule)\b/i,
      /\bnew workflow\b/i,
    ],
    data: [
      /\b(create|define|set up|build)\b.*\b(schema|data group|data model)\b/i,
      /\bnew schema\b/i,
      /\b(move on|move onto|start|go to|lets do|work on)\b.*\b(data|schema)\b/i,
      /\bdata schema\b/i,
    ],
    'page-design': [
      /\b(design|build|create|make|plan)\b.*\b(page|pages|ui|interface|dashboard|screen|layout|view)\b/i,
      /\b(page|pages|ui|screen)\b.*\b(design|layout|structure)\b/i,
      /\blet'?s?\s+(design|build|plan|create)\s+(the\s+)?(page|pages|ui|interface)\b/i,
      /\b(move on|move onto|start|go to|lets do|work on|continue to|continue with|proceed to|proceed with)\b.*\b(page|pages|ui|screen|interface)\b/i,
      // Conversational: wanting specific UI elements implies page design intent
      /\b(i want|i need|add|include|give me)\b.*\b(chart|table|card|form|dashboard|overview)\b/i,
      // Referring to pages by name (e.g., "the home page should...")
      /\b(home|main|detail|overview)\s+(page|screen|view)\b.*\b(should|needs?|will|can)\b/i,
    ],
  };

  // Pre-filter: Broad keyword check. If none match, skip LLM entirely.
  const WIZARD_KEYWORD_PREFILTER = /\b(build|create|make|develop|design|set up|define|deploy|configure|write|compile|start|author|need|want|help|track|manage|monitor|automate|something|tool|app|system|platform|agent|bot|workflow|schema|knowledge|skill|pipeline|automation|assistant|product|solution|project|page|pages|dashboard|screen|layout|view|interface|ui)\b/i;

  // Intent → PipelineMode mapping
  const INTENT_TO_PIPELINE: Record<string, PlanPipelineMode | null> = {
    app: 'knowledge',      // App creation starts with knowledge base wizard
    knowledge: 'knowledge',
    skill: 'skill',
    agent: 'agent',
    workflow: 'workflow',
    data: 'data',
    'page-design': 'page-design',
    none: null,
  };

  // Module-level LRU cache for LLM classification results
  const wizardIntentCache = new Map<string, { intent: string; confidence: number; domainHint: string | null }>();
  const WIZARD_CACHE_MAX = 50;

  /** Classify wizard intent using Tier 1 regex → pre-filter → Tier 2 LLM hybrid. */
  async function classifyWizardIntent(input: string): Promise<{ intent: string; confidence: number; domainHint: string | null }> {
    const NO_MATCH = { intent: 'none', confidence: 1.0, domainHint: null };

    // Tier 1: Fast regex check — specific intents checked BEFORE broad ones
    // (e.g., "data" before "app" so "create a data schema for your app" matches data, not app)
    for (const intent of ['data', 'page-design', 'knowledge', 'skill', 'agent', 'workflow', 'app']) {
      if (WIZARD_REGEX_MAP[intent]?.some(p => p.test(input))) {
        return { intent, confidence: 0.95, domainHint: null };
      }
    }

    // Tier 1.5: Guided flow context — if user completed data step, page design
    // is the natural next step. Route component/layout messages there automatically.
    {
      const flow = parseGuidedFlow();
      if (flow.lastStep === 'data') {
        const PAGE_ADJACENT = /\b(chart|table|card|form|dashboard|overview|weight|metric|log|summary|area chart|tracking|page|layout|component)\b/i;
        if (PAGE_ADJACENT.test(input)) {
          return { intent: 'page-design', confidence: 0.85, domainHint: flow.domain ?? null };
        }
      }
    }

    // Pre-filter: Skip LLM if no creation-adjacent keywords at all
    if (!WIZARD_KEYWORD_PREFILTER.test(input)) {
      return NO_MATCH;
    }

    // Cache check
    const cacheKey = input.trim().toLowerCase().slice(0, 200);
    const cached = wizardIntentCache.get(cacheKey);
    if (cached) return cached;

    // Tier 2: LLM classification
    try {
      const result = await planApi.classifyWizardIntent(input);
      if (result.success && result.intent) {
        const entry = {
          intent: result.intent,
          confidence: result.confidence ?? 0.7,
          domainHint: result.domainHint ?? null,
        };
        // Add to cache (evict oldest if full)
        if (wizardIntentCache.size >= WIZARD_CACHE_MAX) {
          const firstKey = wizardIntentCache.keys().next().value;
          if (firstKey) wizardIntentCache.delete(firstKey);
        }
        wizardIntentCache.set(cacheKey, entry);
        return entry;
      }
      wizardIntentCache.set(cacheKey, NO_MATCH);
      return NO_MATCH;
    } catch (error) {
      // Timeout or network error — safe fallback to no match
      console.warn('[WizardIntent] LLM classification failed, falling back to no-match', error);
      return NO_MATCH;
    }
  }

  const handleSkillWizardMessage = useCallback(async (userInput: string) => {
    addUIMessage(userInput, 'user');
    deps.setInputValue('');
    deps.setIsLoading(true);

    try {
      const controller = createAbortController();
      const signal = controller.signal;
      const isDebug = debug.getState().isEnabled;

      const result = await planApi.skillConverse(
        userInput,
        planStore.conversationHistory,
        planStore.currentThreadId ?? undefined,
        signal,
        isDebug,
      );
      planStore.incrementLLMCalls();

      const response = result.data;

      planStore.addUserMessage(userInput);
      planStore.addAssistantMessage(response.message);
      if (response.threadId) planStore.setThreadId(response.threadId);

      if (response.skillReady && response.proposedSkill) {
        planStore.setProposedSkill(response.proposedSkill);
        planStore.setSkillReady(true);
        advanceStage('reviewing');

        addUIMessage(response.message, 'assistant', {
          source: 'skill-wizard',
          skillReady: true,
        });
      } else {
        addUIMessage(response.message, 'assistant', {
          source: 'skill-wizard',
          ...(response.questions?.length ? { questions: response.questions } : {}),
        });
      }
    } catch (err: unknown) {
      if (!isAbortError(err)) {
        const message = toErrMsg(err, 'Skill wizard conversation failed');
        planStore.setError(message);
        advanceStage('failed');
      }
    } finally {
      deps.setIsLoading(false);
    }
  }, [deps, planStore]);

  // ── Creative Studio Wizard Handlers ──────────────────────────────────────────

  const handleAgentWizardMessage = useCallback(async (userInput: string) => {
    addUIMessage(userInput, 'user');
    deps.setInputValue('');
    deps.setIsLoading(true);
    try {
      const controller = createAbortController();
      const signal = controller.signal;
      const isDebug = debug.getState().isEnabled;
      const result = await planApi.agentConverse(userInput, planStore.conversationHistory, planStore.currentThreadId ?? undefined, signal, isDebug);
      planStore.incrementLLMCalls();
      const response = result.data;
      planStore.addUserMessage(userInput);
      planStore.addAssistantMessage(response.message);
      if (response.threadId) planStore.setThreadId(response.threadId);
      if (response.agentReady && response.proposedAgent) {
        planStore.setProposedAgent(response.proposedAgent);
        planStore.setAgentReady(true);
        advanceStage('reviewing');
        addUIMessage(response.message, 'assistant', { source: 'agent-wizard', agentReady: true });
      } else {
        addUIMessage(response.message, 'assistant', { source: 'agent-wizard', ...(response.questions?.length ? { questions: response.questions } : {}) });
      }
    } catch (err: unknown) {
      if (!isAbortError(err)) {
        planStore.setError(toErrMsg(err, 'Agent wizard conversation failed'));
        advanceStage('failed');
      }
    } finally { deps.setIsLoading(false); }
  }, [deps, planStore]);

  const handleKnowledgeWizardMessage = useCallback(async (userInput: string) => {
    addUIMessage(userInput, 'user');
    deps.setInputValue('');
    deps.setIsLoading(true);
    try {
      // On the first message, search existing knowledge base for relevant articles
      let enrichedInput = userInput;
      if (planStore.conversationHistory.length === 0) {
        try {
          const { searchArticles } = await import('@/services/api/knowledgeApi');
          const existing = await searchArticles(userInput, { limit: 5 });
          if (existing.length > 0) {
            const summaries = existing.map(a => `- "${a.title}" (${a.domain_tags.join(', ')})`).join('\n');
            enrichedInput = `${userInput}\n\n[System context: The user already has these relevant knowledge articles:\n${summaries}\nAcknowledge what already exists and focus on what's new or missing.]`;
          }
        } catch {
          // Non-fatal — proceed without existing knowledge context
        }
      }
      const controller = createAbortController();
      const signal = controller.signal;
      const isDebug = debug.getState().isEnabled;
      const result = await planApi.knowledgeConverse(enrichedInput, planStore.conversationHistory, planStore.currentThreadId ?? undefined, signal, isDebug);
      planStore.incrementLLMCalls();
      const response = result.data;
      planStore.addUserMessage(userInput);
      planStore.addAssistantMessage(response.message);
      if (response.threadId) planStore.setThreadId(response.threadId);
      if (response.knowledgeReady && response.proposedKnowledgeBase) {
        planStore.setProposedKnowledgeBase(response.proposedKnowledgeBase);
        planStore.setKnowledgeReady(true);
        advanceStage('reviewing');
        addUIMessage(response.message, 'assistant', { source: 'knowledge-wizard', knowledgeReady: true });

        // Persist discovered gaps (non-fatal — KB was already confirmed)
        const proposedKB = response.proposedKnowledgeBase;
        const gaps = proposedKB.gaps;
        if (gaps && gaps.length > 0) {
          import('@/services/api/knowledgeApi').then(({ createGapsBatch }) => {
            createGapsBatch(gaps.map((gap: { topic: string; description: string; category: string; gap_type: string; priority: string; suggested_question?: string }) => ({
              topic: gap.topic,
              description: gap.description,
              category: gap.category,
              gap_type: gap.gap_type,
              priority: gap.priority,
              domain_tags: proposedKB.tags || [],
              metadata: {
                discovered_by: 'wizard',
                suggested_question: gap.suggested_question ?? `What should be documented about ${gap.topic}?`,
              },
            }))).catch((err: unknown) => {
              console.warn('Failed to persist knowledge gaps:', err);
            });
          });
        }
      } else {
        addUIMessage(response.message, 'assistant', { source: 'knowledge-wizard', ...(response.questions?.length ? { questions: response.questions } : {}) });
      }
    } catch (err: unknown) {
      if (!isAbortError(err)) {
        planStore.setError(toErrMsg(err, 'Knowledge wizard conversation failed'));
        advanceStage('failed');
      }
    } finally { deps.setIsLoading(false); }
  }, [deps, planStore]);

  const handleWorkflowWizardMessage = useCallback(async (userInput: string) => {
    addUIMessage(userInput, 'user');
    deps.setInputValue('');
    deps.setIsLoading(true);
    try {
      const controller = createAbortController();
      const signal = controller.signal;
      const isDebug = debug.getState().isEnabled;
      // Pass knowledge base context so the workflow wizard understands the domain
      const kb = planStore.proposedKnowledgeBase;
      const knowledgeContext = kb ? { title: kb.title, description: kb.description, domain: kb.domain, articles: kb.articles?.map(a => ({ title: a.title, outline: a.outline })), tags: kb.tags } : undefined;

      // Build schema context so workflow wizard knows available data entities
      const schemaContext = await buildSchemaContext(planStore);

      // Gather canvas component context so workflow wizard knows what's on the canvas
      let componentContext: Array<{ id: string; type: string; name?: string; page?: string }> | undefined;
      try {
        const editorStore = (await import('@/stores/editor/editor-store')).useEditorStore.getState();
        const components = editorStore.components;
        if (components && components.length > 0) {
          componentContext = components.slice(0, 30).map((c: Component) => ({
            id: c.id,
            type: c.type,
            name: (c.name || c.properties?.label || c.properties?.title) as string | undefined,
            page: c.pageId || 'default',
          }));
        }
      } catch { /* non-fatal — proceed without component context */ }

      const result = await planApi.workflowConverse(userInput, planStore.conversationHistory, planStore.currentThreadId ?? undefined, signal, isDebug, knowledgeContext, schemaContext, componentContext);
      planStore.incrementLLMCalls();
      const response = result.data;
      planStore.addUserMessage(userInput);
      planStore.addAssistantMessage(response.message);
      if (response.threadId) planStore.setThreadId(response.threadId);
      if (response.workflowReady && response.proposedWorkflow) {
        planStore.setProposedWorkflow(normalizeAutomationProposal(response.proposedWorkflow));
        planStore.setWorkflowReady(true);
        advanceStage('reviewing');
        addUIMessage(response.message, 'assistant', { source: 'workflow-wizard', workflowReady: true });
      } else {
        addUIMessage(response.message, 'assistant', { source: 'workflow-wizard', ...(response.questions?.length ? { questions: response.questions } : {}) });
      }
    } catch (err: unknown) {
      if (!isAbortError(err)) {
        planStore.setError(toErrMsg(err, 'Workflow wizard conversation failed'));
        advanceStage('failed');
      }
    } finally { deps.setIsLoading(false); }
  }, [deps, planStore]);

  const handleDataWizardMessage = useCallback(async (userInput: string) => {
    addUIMessage(userInput, 'user');
    deps.setInputValue('');
    deps.setIsLoading(true);
    try {
      const controller = createAbortController();
      const signal = controller.signal;
      const isDebug = debug.getState().isEnabled;
      // Pass knowledge base context so the data wizard can infer schema from domain knowledge
      const kb = planStore.proposedKnowledgeBase;
      const knowledgeContext = kb ? { title: kb.title, description: kb.description, domain: kb.domain, articles: kb.articles?.map(a => ({ title: a.title, outline: a.outline })), tags: kb.tags } : undefined;
      const result = await planApi.dataConverse(userInput, planStore.conversationHistory, planStore.currentThreadId ?? undefined, signal, isDebug, knowledgeContext);
      planStore.incrementLLMCalls();
      const response = result.data;
      planStore.addUserMessage(userInput);
      planStore.addAssistantMessage(response.message);
      if (response.threadId) planStore.setThreadId(response.threadId);
      if (response.dataReady && response.proposedSchema) {
        planStore.setProposedSchema(response.proposedSchema);
        planStore.setDataReady(true);
        advanceStage('reviewing');
        addUIMessage(response.message, 'assistant', { source: 'data-wizard', dataReady: true });
        // Update guided flow progress so "what's next?" knows data step is done
        try {
          const flow = parseGuidedFlow();
          sessionStorage.setItem('terramatrix_guided_flow', JSON.stringify({ ...flow, lastStep: 'data' }));
        } catch { /* non-fatal */ }
      } else {
        addUIMessage(response.message, 'assistant', { source: 'data-wizard', ...(response.questions?.length ? { questions: response.questions } : {}) });
      }
    } catch (err: unknown) {
      if (!isAbortError(err)) {
        planStore.setError(toErrMsg(err, 'Data wizard conversation failed'));
        advanceStage('failed');
      }
    } finally { deps.setIsLoading(false); }
  }, [deps, planStore]);

  // ── Page Design Wizard Handler ────────────────────────────────────────

  const handlePageDesignWizardMessage = useCallback(async (userInput: string) => {
    addUIMessage(userInput, 'user');
    deps.setInputValue('');
    deps.setIsLoading(true);
    try {
      const controller = createAbortController();
      const signal = controller.signal;
      const isDebug = debug.getState().isEnabled;

      // Pass knowledge base context
      const kb = planStore.proposedKnowledgeBase;
      const knowledgeContext = kb
        ? { title: kb.title, description: kb.description, domain: kb.domain,
            articles: kb.articles?.map(a => ({ title: a.title, outline: a.outline })),
            tags: kb.tags }
        : undefined;

      // Build schema context from proposed + published schemas
      const schemaContext = await buildSchemaContext(planStore);

      const result = await planApi.pageDesignConverse(
        userInput, planStore.conversationHistory,
        planStore.currentThreadId ?? undefined, signal, isDebug,
        knowledgeContext, schemaContext,
      );
      planStore.incrementLLMCalls();
      const response = result.data;
      planStore.addUserMessage(userInput);
      planStore.addAssistantMessage(response.message);
      if (response.threadId) planStore.setThreadId(response.threadId);

      if (response.designReady) {
        if (response.proposedPageDesign) {
          planStore.setProposedPageDesign(response.proposedPageDesign);
        }
        planStore.setPageDesignReady(true);
        addUIMessage(response.message, 'assistant', { source: 'page-design-wizard', designReady: true });

        // Update guided flow progress
        try {
          const flow = parseGuidedFlow();
          sessionStorage.setItem('terramatrix_guided_flow', JSON.stringify({ ...flow, lastStep: 'pages' }));
        } catch { /* non-fatal */ }

        // Auto-compile into Quick Mode plan for review + execution
        try {
          await runQuickModeCompile(signal);
        } catch (compileErr: unknown) {
          if (!isAbortError(compileErr)) {
            addUIMessage(
              'Your page design was captured, but compiling the execution plan failed. ' +
              'Say "try again" or "compile" to retry.',
              'assistant',
              { source: 'page-design-wizard', error: true },
            );
          }
        }
      } else {
        addUIMessage(response.message, 'assistant', { source: 'page-design-wizard' });
      }
    } catch (err: unknown) {
      if (!isAbortError(err)) {
        planStore.setError(toErrMsg(err, 'Page design conversation failed'));
        advanceStage('failed');
      }
    } finally { deps.setIsLoading(false); }
  }, [deps, planStore]);

  // ── Skill Wizard: confirm creation ───────────────────────────────────────

  const confirmSkillCreation = useCallback(async (activate?: boolean) => {
    const skill = planStore.proposedSkill;
    if (!skill) return;
    deps.setIsLoading(true);
    try {
      const { skillsApi } = await import('@/services/api/agentApi');
      const created = await skillsApi.create({
        name: skill.name,
        description: skill.description,
        instructions: skill.instructions,
        domain: skill.domain,
        tags: skill.tags,
      });
      if (activate && created?.data?.skill_id) {
        await skillsApi.activate(created.data.skill_id);
      }
      advanceStage('complete');
      addUIMessage(
        `Skill "${skill.name}" created successfully${activate ? ' and activated' : ' as draft'}.`,
        'assistant',
        { source: 'skill-wizard' },
      );
    } catch (err: unknown) {
      const message = toErrMsg(err, 'Failed to create skill');
      planStore.setError(message);
      addUIMessage(`Failed to create skill: ${message}`, 'assistant', { source: 'skill-wizard' });
    } finally {
      deps.setIsLoading(false);
    }
  }, [deps, planStore]);

  // ── Agent Wizard: confirm creation ──────────────────────────────────────

  const confirmAgentCreation = useCallback(async (activate?: boolean) => {
    const agent = planStore.proposedAgent;
    if (!agent) return;
    deps.setIsLoading(true);
    try {
      const { frameworkApi } = await import('@/services/api/agentApi');
      const { useAuthStore } = await import('@/stores/auth/auth-store');
      const userId = useAuthStore.getState().user?.id || '';

      const result = await frameworkApi.createAgent({
        description: `${agent.name}: ${agent.description}. Role: ${agent.role}`,
        scope: 'user',
        scope_key: userId,
        parameters: {
          capabilities: agent.capabilities,
          tools: agent.tools,
          domain: agent.domain,
          mqttSubscriptions: agent.mqttSubscriptions,
          tier: agent.tier,
        },
      });

      if (result.data?.status === 'needs_information') {
        const missing = result.data.missing || [];
        const missingNames = missing.map((m) => m.name || String(m)).join(', ');
        addUIMessage(
          `I need a bit more information to create this agent. Missing: ${missingNames}`,
          'assistant',
          { source: 'agent-wizard' },
        );
        return;
      }

      advanceStage('complete');
      addUIMessage(
        `Agent "${agent.name}" created successfully${activate ? ' and activated' : ' as draft'}.\n\nInstance ID: \`${result.data?.instance_id || 'unknown'}\``,
        'assistant',
        { source: 'agent-wizard' },
      );
    } catch (err: unknown) {
      const message = toErrMsg(err, 'Failed to create agent');
      planStore.setError(message);
      addUIMessage(`Failed to create agent: ${message}`, 'assistant', { source: 'agent-wizard' });
    } finally {
      deps.setIsLoading(false);
    }
  }, [deps, planStore]);

  // ── confirmWorkflowCreation ─────────────────────────────────────────────────

  const confirmWorkflowCreation = useCallback(async (publish?: boolean) => {
    const proposedWorkflow = planStore.proposedWorkflow;
    const workflow = proposedWorkflow ? normalizeAutomationProposal(proposedWorkflow) : null;
    if (!workflow) return;
    const safety = workflow.safety;
    const publishAllowed = publish === true && safety?.approvalRequired !== true && safety?.riskLevel !== 'blocked';
    const status = publishAllowed ? 'published' : 'draft';
    deps.setIsLoading(true);
    try {
      const { workflowApi } = await import('@/services/api/workflowApi');
      const result = await workflowApi.createWorkflow({
        name: workflow.name,
        description: workflow.description,
        status,
        definition: {
          nodes: [],
          edges: [],
          triggers: workflow.triggerConfig ? [{
            id: `trigger-${Date.now()}`,
            type: workflow.triggerType,
            config: workflow.triggerConfig,
            enabled: publishAllowed && safety?.disabledByDefault !== true,
          }] : [],
          variables: [],
        },
        tags: workflow.tags,
        metadata: {
          customFields: {
            createdBy: 'workflow-wizard',
            automationSafety: safety,
            proposalState: status,
            approvalRequired: safety?.approvalRequired ?? false,
            draftByDefault: safety?.draftByDefault ?? status === 'draft',
            disabledByDefault: safety?.disabledByDefault ?? status === 'draft',
          },
        },
      });
      const workflowId = result.data?.id || result.data?._id;
      advanceStage('complete');
      addUIMessage(
        `Workflow "${workflow.name}" created successfully as ${status}.\n\nYou can open it in the [Workflow Editor](/workflow-editor/${workflowId}) to add nodes and logic.`,
        'assistant',
        { source: 'workflow-wizard' },
      );
    } catch (err: unknown) {
      const message = toErrMsg(err, 'Failed to create workflow');
      planStore.setError(message);
      addUIMessage(`Failed to create workflow: ${message}`, 'assistant', { source: 'workflow-wizard' });
    } finally {
      deps.setIsLoading(false);
    }
  }, [deps, planStore]);

  // ── confirmDataSchemaCreation ───────────────────────────────────────────────

  const confirmDataSchemaCreation = useCallback(async () => {
    const schema = planStore.proposedSchema;
    if (!schema) return;
    deps.setIsLoading(true);
    try {
      const { schemaApi, generateSchemaName } = await import('@/services/api/schemaApi');
      const validFieldTypes = new Set(FIELD_TYPES.map(ft => ft.value));
      const fields = (schema.fields || []).map((f, i) => ({
        id: `field_${Date.now()}_${i}`,
        name: f.name,
        type: validFieldTypes.has(f.type as typeof FIELD_TYPES[number]['value'])
          ? f.type as typeof FIELD_TYPES[number]['value']
          : 'string' as const,
        required: f.required ?? false,
        description: f.description,
      }));
      await schemaApi.createSchema({
        name: generateSchemaName(schema.name),
        displayName: schema.name,
        description: schema.description,
        fields,
        visibility: 'private' satisfies SchemaVisibility,
        tags: schema.tags,
      });
      advanceStage('complete');
      addUIMessage(
        `Data schema "${schema.name}" created successfully with ${fields.length} field(s).`,
        'assistant',
        { source: 'data-wizard' },
      );
    } catch (err: unknown) {
      const message = toErrMsg(err, 'Failed to create schema');
      planStore.setError(message);
      addUIMessage(`Failed to create schema: ${message}`, 'assistant', { source: 'data-wizard' });
    } finally {
      deps.setIsLoading(false);
    }
  }, [deps, planStore]);

  // ── tryAutoActivate ─────────────────────────────────────────────────────────
  // Detects creation intents in regular chat and auto-activates Plan Mode
  // so the user doesn't have to manually toggle it first.
  // Returns true if Plan Mode was activated and the message was handled.

  const tryAutoActivate = useCallback(async (userInput: string): Promise<boolean> => {
    if (planStore.isActive) return false;

    const { intent } = await classifyWizardIntent(userInput);
    const pipelineMode = INTENT_TO_PIPELINE[intent] ?? null;

    if (!pipelineMode) return false;

    const activated = planStore.activate();
    if (!activated) return false; // Lock held by another tab

    planStore.setPipelineMode(pipelineMode);

    // Route directly to the wizard handler — don't go through handlePlanMessage
    // which would re-classify due to stale planStore closure
    if (pipelineMode === 'skill') { await handleSkillWizardMessage(userInput); return true; }
    if (pipelineMode === 'agent') { await handleAgentWizardMessage(userInput); return true; }
    if (pipelineMode === 'knowledge') { await handleKnowledgeWizardMessage(userInput); return true; }
    if (pipelineMode === 'workflow') { await handleWorkflowWizardMessage(userInput); return true; }
    if (pipelineMode === 'data') { await handleDataWizardMessage(userInput); return true; }
    if (pipelineMode === 'page-design') { await handlePageDesignWizardMessage(userInput); return true; }

    return true;
  }, [planStore, handleSkillWizardMessage, handleAgentWizardMessage, handleKnowledgeWizardMessage, handleWorkflowWizardMessage, handleDataWizardMessage, handlePageDesignWizardMessage]);

  return {
    handleSkillWizardMessage,
    handleAgentWizardMessage,
    handleKnowledgeWizardMessage,
    handleWorkflowWizardMessage,
    handleDataWizardMessage,
    handlePageDesignWizardMessage,
    tryAutoActivate,
    confirmSkillCreation,
    confirmAgentCreation,
    confirmWorkflowCreation,
    confirmDataSchemaCreation,
    classifyWizardIntent,
    INTENT_TO_PIPELINE,
  };
}
